import React from 'react';
import CTASection from '../components/CTASection';
import SEOMetaTags from '../components/SEOMetaTags';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const About = () => {
    const { t } = useLanguage();
    usePageTitle('about');

    const values = [
        { title: t('about.values.value1.title'), description: t('about.values.value1.description') },
        { title: t('about.values.value2.title'), description: t('about.values.value2.description') },
        { title: t('about.values.value3.title'), description: t('about.values.value3.description') }
    ];

    const teamMembers = [
        {
            name: t('about.team.member1.name'),
            rolePrefix: t('about.team.member1.rolePrefix'),
            roleSuffix: t('about.team.member1.roleSuffix'),
            roleSpace: t('about.team.member1.roleSpace'),
            description: t('about.team.member1.description')
        },
        {
            name: t('about.team.member2.name'),
            rolePrefix: t('about.team.member2.rolePrefix'),
            roleSuffix: t('about.team.member2.roleSuffix'),
            roleSpace: t('about.team.member2.roleSpace'),
            description: t('about.team.member2.description')
        },
        {
            name: t('about.team.member3.name'),
            rolePrefix: t('about.team.member3.rolePrefix'),
            roleSuffix: t('about.team.member3.roleSuffix'),
            roleSpace: t('about.team.member3.roleSpace'),
            description: t('about.team.member3.description')
        }
    ];

    return (
        <>
            <SEOMetaTags pageKey="about" />
            {/* Section - Inner Page Headline Start */}
            <div className="mxd-section mxd-section-inner-headline padding-text-pre-block">
                <div className="mxd-container grid-container">
                    <div className="mxd-block loading-wrap">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-inner-headline loading__item">
                                        <p className="mxd-point-subtitle">
                                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                                                width="20px" height="20px" viewBox="0 0 20 20" fill="currentColor">
                                                <path fill="currentColor" d="M19.6,9.6c0,0-3,0-4,0c-0.4,0-1.8-0.2-1.8-0.2c-0.6-0.1-1.1-0.2-1.6-0.6c-0.5-0.3-0.9-0.8-1.2-1.2
                        c-0.3-0.4-0.4-0.9-0.5-1.4c0,0-0.1-1.1-0.2-1.5c-0.1-1.1,0-4.4,0-4.4C10.4,0.2,10.2,0,10,0S9.6,0.2,9.6,0.4c0,0,0.1,3.3,0,4.4
                        c0,0.4-0.2,1.5-0.2,1.5C9.4,6.7,9.2,7.2,9,7.6C8.7,8.1,8.2,8.5,7.8,8.9c-0.5,0.3-1,0.5-1.6,0.6c0,0-1.2,0.1-1.7,0.2
                        c-1,0.1-4.2,0-4.2,0C0.2,9.6,0,9.8,0,10c0,0.2,0.2,0.4,0.4,0.4c0,0,3.1-0.1,4.2,0c0.4,0,1.7,0.2,1.7,0.2c0.6,0.1,1.1,0.2,1.6,0.6
                        c0.4,0.3,0.8,0.7,1.1,1.1c0.3,0.5,0.5,1,0.6,1.6c0,0,0.1,1.3,0.2,1.7c0,1,0,4.1,0,4.1c0,0.2,0.4-0.2,0.4-0.4s0.4-0.2,0.4-0.4
                        c0,0,0-3.1,0-4.1c0-0.4,0.2-1.7,0.2-1.7c0.1-0.6,0.2-1.1,0.6-1.6c0.3-0.4,0.7-0.8,1.1-1.1c0.5-0.3,1-0.5,1.6-0.6
                        c0,0,1.3-0.1,1.8-0.2c1,0,4,0,4,0c0.2,0,0.4-0.2,0.4-0.4C20,9.8,19.8,9.6,19.6,9.6L19.6,9.6z" />
                                            </svg>
                                            <span>{t('about.hero.badge')}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-8 mxd-grid-item no-margin">
                                    <div className="mxd-block__content">
                                        <div className="mxd-block__inner-headline">
                                            <h1 className="inner-headline__title animated-type loading__item">
                                                <span id="typed-strings">
                                                    <b>{t('about.hero.typed1')}</b>
                                                    <b>{t('about.hero.typed2')}</b>
                                                    <b>{t('about.hero.typed3')}</b>
                                                </span>
                                                <span id="typed"></span>
                                            </h1>
                                            <p className="inner-headline__text t-large t-bright loading__item">
                                                {t('about.hero.description')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Section - Inner Page Headline End */}

            {/* Section - About Description Start */}
            <div className="mxd-section padding-pre-grid">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="reveal-type">{t('about.mission.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{t('about.mission.description')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Section - About Description End */}

            {/* Section - Values Start */}
            <div className="mxd-section padding-default">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-approach-list">
                            {values.map((value, index) => (
                                <div key={index} className="mxd-approach-list__item">
                                    <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                    <div className="mxd-approach-list__inner">
                                        <div className="container-fluid px-0">
                                            <div className="row gx-0">
                                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                                    <div className="mxd-approach-list__image anim-uni-in-up"></div>
                                                </div>
                                                <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                    <div className="mxd-approach-list__title anim-uni-in-up">
                                                        <h6>{value.title}</h6>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                                    <div className="mxd-approach-list__descr anim-uni-in-up">
                                                        <p>{value.description}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {/* Section - Values End */}

            {/* Section - Team Start */}
            <div className="mxd-section padding-default">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-pinned-universal">
                            <div className="container-fluid px-0">
                                <div className="row gx-0">
                                    <div className="col-12 col-xl-5 mxd-pinned-universal__static">
                                        <div className="mxd-pinned-universal__static-inner no-margin">
                                            <div className="mxd-section-title h2-only no-margin-desktop">
                                                <div className="container-fluid p-0">
                                                    <div className="row g-0">
                                                        <div className="col-12 mxd-grid-item no-margin">
                                                            <div className="mxd-section-title__title card-split-title">
                                                                <h2 className="reveal-type">{t('about.team.title')}</h2>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-7 mxd-pinned-universal__scroll">
                                        <div className="mxd-pinned-universal__scroll-inner mxd-grid-item no-margin">
                                            <div className="mxd-res-list">
                                                {teamMembers.map((member, index) => (
                                                    <div key={index} className="mxd-res-list__item">
                                                        <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                        <div className="mxd-res-list__content">
                                                            <div className="mxd-res-list__data">
                                                                <div className="mxd-res-list__title">
                                                                    <h4 className="anim-uni-in-up">{member.name}</h4>
                                                                    <p className="mxd-res-list__source anim-uni-in-up">
                                                                        {member.rolePrefix}{member.roleSpace ? ' ' : ''}
                                                                        <a href="#0" target="_blank" rel="noopener noreferrer">{member.roleSuffix}</a>
                                                                    </p>
                                                                </div>
                                                                <div className="mxd-res-list__descr">
                                                                    <p className="anim-uni-in-up">{member.description}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Section - Team End */}

            {/* Section - CTA Start */}
            <CTASection
                title={t('about.cta.title')}
                description={t('about.cta.description')}
                buttonText={t('about.cta.button')}
                icon="ph-bold ph-question"
                iconType="icon"
            />
            {/* Section - CTA End */}
        </>
    );
};

export default About;
